import { Injectable } from '@angular/core';
import { LocalStorageService } from 'src/modules/shared/services/local-storage.service';
import { Platform } from '@ionic/angular';
import { Geolocation, Geoposition } from '@ionic-native/geolocation/ngx';
import { Diagnostic } from '@ionic-native/diagnostic/ngx';
import { PopupService } from 'src/modules/shared/services/popup.service';
import { JobService } from 'src/modules/shared/services/job.service';
import { LoadingService } from 'src/modules/shared/services/loading.service';

// Generous enough for someone to read the iOS system dialog on a busy job site, short
// enough that a prompt that never resolves cannot strand them behind the loading spinner.
const AUTHORIZATION_PROMPT_TIMEOUT_MS = 60000;

@Injectable({ providedIn: 'root' })
export class TalentLocationTrackingService {
  constructor(
    private localStorageService: LocalStorageService,
    private platform: Platform,
    private popupService: PopupService,
    private jobService: JobService,
    private loadingService: LoadingService,
    private geolocation: Geolocation,
    private diagnostic: Diagnostic
  ) {}

  // DEPRECATED -- DO NOT CHANGE IT OR WORK ON IT
  async checkIfUserIn100meters(jobId, operation: 'start' | 'end', success, error) {
    const isBrowser = window.location.href.indexOf('app.staffy.com') > -1;
    const isCordova = this.platform.is('cordova');
    const user = await this.localStorageService.getLoggedInUserInfo();

    if (isCordova && !isBrowser && user) {
      const loading = await this.loadingService.showLoading().toPromise();

      this.diagnostic.isLocationEnabled().then(
        isEnabled => {
          if (!isEnabled) {
            this.loadingService.hideLoading(loading);
            this.showLocationPermissionDenialPopup();
            return;
          }
          // location is enabled
          // now try to get user's current position
          this.geolocation
            .getCurrentPosition({
              enableHighAccuracy: true
            })
            .then(
              // got the current position
              async resp => {
                try {
                  // ensuring that the user is within 150 meters of the location
                  await this.jobService.ensureLocation(user._id, jobId, {
                    lat: resp.coords.latitude,
                    lng: resp.coords.longitude,
                    operation
                  });
                  // user is within 150 meters
                  success();
                } catch (err) {
                  // user is not at the location, show error
                  console.log('err', err);
                  error();
                } finally {
                  this.loadingService.hideLoading(loading);
                }
              }
            )
            .catch(error => {
              // unable to retrive the location
              // maybe user denied the permission, so show the permission
              // message
              this.loadingService.hideLoading(loading);
              this.showLocationPermissionDenialPopup(`Please enable location access for staffy`);
              console.log('Error getting location', error);
            });
        },
        err => {
          // location is not enabled or failed to get the location enabled status
          this.loadingService.hideLoading(loading);
          this.showLocationPermissionDenialPopup();
        }
      );
    } else {
      success();
    }
  }

  // This is refactored version,
  async ensureUserIn100meters(jobId, operation: 'start' | 'end'): Promise<Geoposition> {
    const loading = await this.loadingService.showLoading().toPromise();

    try {
      const isBrowser = window.location.href.indexOf('app.staffy.com') > -1;
      const isCordova = this.platform.is('cordova');
      const user = await this.localStorageService.getLoggedInUserInfo();

      if (!isCordova && !user) {
        // because we can't determine the location
        throw new Error(`can't determine user's location`);
      }

      await this.ensureLocationIsEnabled();
      // location is enabled
      // now try to get user's current position (getUserLocation settles the permission
      // prompt first, so callers that skip this method are covered too)
      const userLocation = await this.getUserLocation();
      // got the user location
      await this.ensureUserIsOnSite(user, jobId, userLocation, operation);
      // user is on site
      return userLocation;
    } catch (e) {
      console.log('error in checking user location', e);
      // rethrow the error
      throw e;
    } finally {
      // hide the loader
      this.loadingService.hideLoading(loading);
    }
  }

  showLocationPermissionDenialPopup(message?: string) {
    const instructions = this.getLocationInstructions();
    const fullMessage = `${message || 'You need to enable location access to continue.'}\n\n${instructions}`;
   
    this.popupService.showModal(
      {
        heading: 'Location Access',
        message: fullMessage,
        btn: 'Enable Location',
        navigateRoute: null,
        imgURL: 'assets/images/tick-mark.png',
        specificClass: 'location-permission-popup'
      },
      () => {
        this.openLocationSettings();
      }
    );
  }

  // cordova.plugins.diagnostic only ships switchToLocationSettings() on Android and
  // Windows -- there is no iOS implementation, so on iOS the call rejected and the
  // "Enable Location" button did nothing. iOS has no public API for deep-linking into
  // Privacy > Location Services, so send the user to the app's own settings pane
  // instead, which contains the Location row.
  private async openLocationSettings() {
    try {
      if (this.platform.is('ios')) {
        await this.diagnostic.switchToSettings();
      } else {
        await this.diagnostic.switchToLocationSettings();
      }
    } catch (e) {
      console.log('Error opening location settings', e);
      try {
        if (this.platform.is('ios')) {
          (window as any).open('app-settings:');
        } else {
          (window as any).open('intent://settings#Intent;action=android.settings.LOCATION_SOURCE_SETTINGS;end');
        }
      } catch (fallbackError) {
        console.log('Fallback for opening location settings also failed', fallbackError);
      }
    }
  }

  // ENHANCEMENT 3: Platform-specific instructions
  private getLocationInstructions(): string {
    if (this.platform.is('ios')) {
      return 'Tap Enable Location to open Staffy in Settings, then choose Location and select "While Using the App".';
    } else if (this.platform.is('android')) {
      return 'Go to Settings > Location and enable access for this app.';
    } else {
      return 'Enable location in your device settings to continue.';
    }
  }

  // isLocationEnabled() above only reports the device-wide Location Services toggle, so
  // the app permission is still undecided at this point on a first run, or after the user
  // picked "Allow Once" and that grant lapsed. Leaving it to getCurrentPosition() means its
  // 15s timeout runs while the contractor is still reading the iOS system dialog, and losing
  // that race sends them to Settings even though nothing is actually wrong. Settle the
  // prompt here instead -- requestLocationAuthorization() has no timeout of its own.
  async ensureLocationIsAuthorized() {
    // The diagnostic plugin only exists on device. On the web build the browser handles its
    // own permission prompt, so leave that path exactly as it was.
    if (!this.platform.is('cordova')) {
      return;
    }

    let status: string;

    try {
      status = await this.diagnostic.getLocationAuthorizationStatus();
    } catch (error) {
      // Status is unreadable, so let getCurrentPosition() drive the prompt as before.
      console.log('Error getting location authorization status', error);
      return;
    }

    if (status === this.diagnostic.permissionStatus.NOT_REQUESTED) {
      try {
        // No argument means "when in use" -- do not request ALWAYS, the app has no
        // background location feature and NSLocationAlwaysAndWhenInUseUsageDescription
        // is deliberately absent from the Info.plist.
        //
        // The bound matters: the plugin only resolves this call when the authorization
        // status actually *changes* (Diagnostic_Location.m, reportChangeAuthorizationStatus).
        // If the contractor backgrounds the app with the dialog up, or iOS suppresses the
        // prompt, the status never changes and the call never settles -- which would strand
        // them behind the loading spinner with no way out but force-quitting.
        status = await this.withTimeout(
          this.diagnostic.requestLocationAuthorization(),
          AUTHORIZATION_PROMPT_TIMEOUT_MS,
          'Timed out waiting for the location authorization prompt'
        );
      } catch (error) {
        // Covers the timeout too. Fall through rather than block the shift: by now the
        // permission may well have been granted, and getCurrentPosition() applies its own
        // timeout and surfaces the denial popup if it really is unavailable.
        console.log('Error or timeout requesting location authorization', error);
        return;
      }
    }

    // On iOS both "denied" and "restricted" are reported as DENIED_ALWAYS; the RESTRICTED
    // branch only ever fires on Android.
    if (
      status === this.diagnostic.permissionStatus.DENIED_ALWAYS ||
      status === this.diagnostic.permissionStatus.RESTRICTED
    ) {
      this.showLocationPermissionDenialPopup();
      throw new Error('Location permission denied');
    }
  }

  private withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
    let timer: any;
    const timeout = new Promise<T>((_resolve, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    });

    return Promise.race([promise, timeout]).then(
      value => {
        clearTimeout(timer);
        return value;
      },
      error => {
        clearTimeout(timer);
        throw error;
      }
    );
  }

  async ensureLocationIsEnabled() {
    try {
      const isEnabled = await this.diagnostic.isLocationEnabled();
      if (!isEnabled) {
        this.showLocationPermissionDenialPopup();
        throw new Error('Location is not enabled');
      }
    } catch (error) {
      console.log('Error getting location settings', error);
      this.showLocationPermissionDenialPopup();
      throw new Error('Error in getting location settings');
    }

    return;
  }

  async getUserLocation() {
    // Settle the permission prompt here rather than in ensureUserIn100meters(): this is the
    // choke point every live flow actually goes through -- shift start, sync, notifications
    // and the home page address lookup all call getUserLocation() directly.
    await this.ensureLocationIsAuthorized();

    try {
      const userLocation = await this.geolocation.getCurrentPosition({
        timeout: 15000, // Add 15 second timeout
        enableHighAccuracy: true
      });
      console.log('in user location: ', userLocation);
      return userLocation;
    } catch (e) {
      console.log('error while getching user location: ', e);
      this.showLocationPermissionDenialPopup();
      throw new Error('Error in getting location');
    }
  }

  private async ensureUserIsOnSite(user, jobId, userLocation, operation) {
    // ensuring that the user is within 150 meters of the location
    try {
      await this.jobService.ensureLocation(user._id, jobId, {
        lat: userLocation.coords.latitude,
        lng: userLocation.coords.longitude,
        operation
      });
      // user is within 150 meters
    } catch (err) {
      this.showInAccurateLocationPopup();
      throw new Error('User is not within 150meters');
    }
  }

  private showInAccurateLocationPopup() { 
    this.popupService.showModal({
      heading: 'Sorry',
      message: `You can only start this shift within 150 metres of the location.`,
      btn: 'Dismiss',
      navigateRoute: null,
      imgURL: 'assets/images/sorry1.png'
    });
  }
}
